import type { RocadeWay } from "@radarspotter/types";
import { Bell, BellOff, BellRing } from "lucide-react";
import { useEffect, useState } from "react";
import { deleteSubscription, fetchSubscriptionWays, saveSubscription } from "@/lib/api";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

const pushSupported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

const ALL_WAYS: RocadeWay[] = ["Intérieure", "Extérieure"];

/** Convertit une clé VAPID base64url en BufferSource pour `applicationServerKey`. */
function urlBase64ToUint8Array(base64: string) {
	const padding = "=".repeat((4 - (base64.length % 4)) % 4);
	const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(normalized);
	return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function currentSubscription() {
	const reg = await navigator.serviceWorker.ready;
	return reg.pushManager.getSubscription();
}

/**
 * Cloche des notifications. Abonné, elle ouvre un panneau pour choisir les sens
 * suivis (rocade intérieure / extérieure) ou se désabonner.
 */
export default function NotifyButton() {
	// null = pas abonné ; sinon les sens suivis.
	const [ways, setWays] = useState<RocadeWay[] | null>(null);
	const [busy, setBusy] = useState(false);
	const [open, setOpen] = useState(false);

	// Au démarrage : relit les préférences côté serveur. Un abonnement que le
	// serveur ne connaît pas (élagué, ou perdu) y est réenregistré.
	useEffect(() => {
		if (!pushSupported) return;
		currentSubscription()
			.then(async (sub) => {
				if (!sub) return;
				const known = await fetchSubscriptionWays(sub.endpoint);
				if (known) return setWays(known);
				await saveSubscription(sub, ALL_WAYS);
				setWays(ALL_WAYS);
			})
			.catch((err) => console.error("Lecture de l'abonnement impossible:", err));
	}, []);

	if (!pushSupported || !VAPID_PUBLIC_KEY) return null;

	async function subscribe() {
		setBusy(true);
		try {
			const permission = await Notification.requestPermission();
			if (permission !== "granted") return;
			const reg = await navigator.serviceWorker.ready;
			const sub = await reg.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
			});
			await saveSubscription(sub, ALL_WAYS);
			setWays(ALL_WAYS);
			setOpen(true);
		} catch (err) {
			console.error("Abonnement push impossible:", err);
		} finally {
			setBusy(false);
		}
	}

	async function unsubscribe() {
		setBusy(true);
		try {
			const sub = await currentSubscription();
			if (sub) {
				await deleteSubscription(sub.endpoint);
				await sub.unsubscribe();
			}
			setWays(null);
			setOpen(false);
		} catch (err) {
			console.error("Désabonnement impossible:", err);
		} finally {
			setBusy(false);
		}
	}

	async function toggleWay(way: RocadeWay) {
		if (!ways) return;
		const next = ways.includes(way)
			? ways.filter((w) => w !== way)
			: ALL_WAYS.filter((w) => w === way || ways.includes(w));
		// Plus aucun sens suivi = plus de notifications.
		if (next.length === 0) return unsubscribe();
		setBusy(true);
		try {
			const sub = await currentSubscription();
			if (sub) await saveSubscription(sub, next);
			setWays(next);
		} catch (err) {
			console.error("Mise à jour des préférences impossible:", err);
		} finally {
			setBusy(false);
		}
	}

	const subscribed = ways !== null;
	const Icon = busy ? Bell : subscribed ? BellRing : BellOff;

	return (
		<div className="fixed top-[calc(15px+env(safe-area-inset-top))] right-[15px] z-[1000] flex flex-col items-end gap-2">
			<button
				type="button"
				disabled={busy}
				onClick={subscribed ? () => setOpen(!open) : subscribe}
				aria-label={subscribed ? "Réglages des notifications" : "Activer les notifications"}
				aria-expanded={subscribed ? open : undefined}
				className="flex size-11 items-center justify-center rounded-full border border-white/10 bg-popover text-foreground shadow-lg backdrop-blur-md transition-colors disabled:opacity-50"
			>
				<Icon className={`size-5 ${subscribed ? "text-blue-400" : ""}`} />
			</button>

			{subscribed && open && (
				<div className="flex w-56 flex-col gap-2 rounded-lg border border-white/10 bg-popover/95 p-3 text-sm shadow-lg backdrop-blur-md">
					<span className="text-xs opacity-70">Me prévenir des radars sur :</span>
					{ALL_WAYS.map((way) => (
						<label key={way} className="flex cursor-pointer items-center gap-2">
							<input
								type="checkbox"
								checked={ways.includes(way)}
								disabled={busy}
								onChange={() => toggleWay(way)}
								className="size-4 accent-blue-500"
							/>
							rocade {way.toLowerCase()}
						</label>
					))}
					<button
						type="button"
						disabled={busy}
						onClick={unsubscribe}
						className="mt-1 rounded-md border border-white/10 py-1.5 text-xs text-red-300 hover:bg-white/5 disabled:opacity-50"
					>
						Désactiver les notifications
					</button>
				</div>
			)}
		</div>
	);
}
